<template>
  <section class="payments-return-section">
    <Preloader v-if="isLoading" />
    <div class="payments-return-wrapper" :class="[`bg-${$route.meta.background}`]">
      <img class="es-logo" src="@/assets/images/brand/logo_full.svg" />
      <form class="payments-return" @submit.prevent="submit" name="payments-return-form">
        <div class="payments-return__logo">
          <h3 class="payments-return__title">
            {{ $t("dashboard.sidebar_account_payments_returns") }}
          </h3>
          <div class="payments-return__description-wrapper">
            <img class="information-icon" alt="Information icon" src="@/assets/images/icons/i_badge.svg" />
            <p class="payments-return__description">
              {{ $t("dashboard.payments_returns_description") }}
            </p>
          </div>
        </div>

        <!-- Editable inputs -->
        <div v-if="isEditing">
          <div class="payments-return__inputs">
            <BaseInput v-model="returnsName" @input="delayTouch($v.returnsName)" name="returnsName" type="text"
              :error="getReturnsNameError()">{{ $t("auth.refunds_name_input") }}</BaseInput>
            <BaseInput v-model="bankAccount" @input="delayTouch($v.bankAccount)" name="bankAccount"
              :error="getBankAccountError()" :mask="'## #### #### #### #### #### ####'">{{ $t("auth.refunds_bank_input")
              }}</BaseInput>
          </div>
          <div v-if="hasStudents">
            <span v-if="additionalDataError" class="error-wrapper">
              <img class="information-icon" src="@/assets/images/icons/i_error.svg" />
              {{ additionalDataError }}
            </span>
            <label class="payments-return__checkbox-wrapper">
              <input class="payments-return__checkbox" type="checkbox" v-model="expandAdditionalData" />
              <span>{{ $t("auth.refunds_additional_children_label") }}</span>
            </label>
            <div v-if="expandAdditionalData" class="payments-return__additional">
              <span>{{ $t("auth.refunds_select_additional_children") }}</span>
              <div class="payments-return__additional-inputs">
                <StudentItem v-for="student in filteredStudents" :key="student.id" :student="student"
                  @click.native="selectStudent(student)"
                  :isSelected="selectedChildren.some(child => child.id === student.id)" isSmall />
              </div>
            </div>
          </div>
          <template v-if="isFinished && requestError">
            <Notification class="error-notification" type="error" description="">
              <span class="notification-title">{{ $t("auth.refunds_data_error_general") }}</span>
            </Notification>
          </template>
        </div>

        <!-- Existing data -->
        <div v-else class="payments-return__data">
          <div class="payments-return__info">
            <span class="label">{{ $t("auth.refunds_data_name_label") }}</span>
            <span class="value">{{ returnsName }}</span>
          </div>
          <div class="payments-return__info">
            <span class="label">{{ $t("auth.refunds_data_bank_label") }}</span>
            <span class="value">{{ bankAccount }}</span>
          </div>
          <template v-if="isFinished && !requestError">
            <Notification class="success-notification" type="success" description="">
              <span class="notification-title">{{ $t("auth.refunds_data_saved_for") }}</span>
              <ul class="notification-list">
                <li class="notification-list-item" v-for="child in selectedChildren" :key="child.id">
                  {{ child.firstName }}
                </li>
              </ul>
            </Notification>
          </template>
        </div>

        <!-- Save data button -->
        <Button v-if="isEditing" :disabled="isSubmiting || isSendButtonActive()" class="btn_primary submit-button">{{
          $t("auth.save_data") }}</Button>

        <!-- Edit button -->
        <Button @click.prevent="isEditing = true" v-else class="btn_primary submit-button">{{
          $t("auth.edit_data")
          }}</Button>
      </form>
    </div>
  </section>
</template>

<script>
import { validationMixin } from 'vuelidate';
import { required } from 'vuelidate/lib/validators';
import {
  mapState, mapActions, mapGetters, mapMutations,
} from 'vuex';
// eslint-disable-next-line import/no-cycle
import VueStore from '@/store';
import Preloader from '@/components/Preloader.vue';
import Button from '@/components/Form/BaseButton.vue';
import BaseInput from '@/components/Form/BaseInput.vue';
import StudentItem from '@/components/StudentItem.vue';
import Notification from '@/components/Notification/Notification.vue';

const touchMap = new WeakMap();

const isValidNameSurnameCombo = value => /^[\p{L}\s-]+$/u.test(value);

export default {
  name: 'PaymentReturn',
  mixins: [validationMixin],
  validations: {
    returnsName: { required, isValidNameSurnameCombo },
    bankAccount: { required },
  },
  data() {
    return {
      returnsNameError: '',
      bankAccountError: '',
      additionalDataError: '',
      hasInitialData: false,
      bankAccount: null,
      returnsName: null,
      isFinished: false,
      requestError: false,
      isSubmiting: false,
      selectedChildren: [],
      expandAdditionalData: false,
      isEditing: false,
    };
  },
  components: {
    Button,
    Preloader,
    BaseInput,
    StudentItem,
    Notification,
  },

  computed: {
    ...mapState('students', ['students', 'student']),
    ...mapState('layout', ['isLoading']),
    ...mapGetters('auth', ['isFullAccess']),

    hasStudents() {
      return !!this.students && this.students.length > 1;
    },

    filteredStudents() {
      return this.students.filter(student => student.id !== this.student.id);
    },
  },

  methods: {
    ...mapActions('auth', ['getRefundData', 'updateRefundData']),
    ...mapMutations('auth', ['setCurrentProfile', 'setAccess']),
    ...mapMutations({
      setCurrentProfile: 'auth/setCurrentProfile',
      setAccess: 'auth/setAccess',
      closeSidebar: 'layout/closeSidebar',
    }),

    getBankAccountLengthValidationHelper(bankAccount) {
      const parsedBankAccountNumber = bankAccount.replace(/\s/g, '');

      if (!this.$v.bankAccount.required) return 'Numer konta bankowego jest wymagany.';
      if (parsedBankAccountNumber.length !== 26) { return 'Podany numer konta powinien składać się z 26 cyfr.'; }

      return undefined;
    },

    getBankAccountError() {
      if (this.$v.bankAccount.$dirty) {
        return (this.getBankAccountLengthValidationHelper(this.$v.bankAccount.$model));
      }
      return undefined;
    },

    getReturnsNameError() {
      if (this.$v.returnsName.$dirty) {
        if (!this.$v.returnsName.required) return 'Imię i nazwisko odbiorcy jest wymagane.';
        if (!this.$v.returnsName.isValidNameSurnameCombo) { return 'Podane imię i nazwisko odbiorcy jest nieprawidłowe. Powinno składać się z liter.'; }
      }
      return undefined;
    },

    getExpandAdditionalDataError() {
      if (this.expandAdditionalData) {
        if (this.selectedChildren.length <= 1) {
          this.additionalDataError = 'Wybierz co najmniej jedno dziecko lub odznacz tę opcję, by zapisać dane';
          return 'Wybierz co najmniej jedno dziecko lub odznacz tę opcję, by zapisać danechuj';
        }
      }

      this.additionalDataError = '';
      return undefined;
    },

    validateForm() {
      this.$v.$touch();

      if (this.$v.$anyError || this.getBankAccountLengthValidationHelper(this.$v.bankAccount.$model)) {
        return false;
      }
      return true;
    },

    isSendButtonActive() {
      return (
        this.$v.$anyError
        || this.$v.$pending
        || !!this.getExpandAdditionalDataError() // Check for expandAdditionalData error
        || (this.expandAdditionalData && this.selectedChildren.length === 0) // Check if checkbox is checked but no children are selected
      );
    },

    delayTouch($v) {
      $v.$reset();
      if (touchMap.has($v)) clearTimeout(touchMap.get($v));
      touchMap.set($v, setTimeout($v.$touch, 800));
    },

    selectStudent(student) {
      const index = this.selectedChildren.findIndex(child => child.id === student.id);
      if (index === -1) {
        this.selectedChildren.push({ id: student.id, firstName: student.firstName });
      } else {
        this.selectedChildren.splice(index, 1);
      }
    },

    async submit() {
      if (!this.validateForm()) {
        return;
      }

      this.isSubmiting = true;

      try {
        const success = await this.updateRefundData({
          kidId: this.student.id,
          fullName: this.returnsName,
          bankAccountNumber: this.bankAccount,
          students: this.selectedChildren.map(student => student.id),
        });

        if (success) {
          this.isFinished = true;
          this.isEditing = false;
        } else {
          this.isFinished = true;
          this.requestError = true;
        }
      } catch (e) {
        if (e?.response && e?.response?.status === 400) {
          console.log(
            `Wystąpił błąd (${e.response.status}) - ${this.error || this.errorOldPassword}`,
          );
        } else {
          console.log(e);
        }
      } finally {
        this.isSubmiting = false;
      }
    },
  },


  beforeRouteEnter(to, from, next) {
    const queryId = to?.query?.id;
    const selectedStudent = VueStore.getters['students/getStudent'];
    // eslint-disable-next-line radix
    const selectedStudentById = VueStore.getters['students/getKidById'](parseInt(queryId));

    if (queryId && !selectedStudentById) {
      next({ name: 'Auth.Welcome' });
    }

    if (selectedStudentById && queryId && queryId !== selectedStudent?.id) {
      VueStore.commit('students/setStudent', selectedStudentById);
      VueStore.commit('auth/setCurrentProfile', 'ROLE_PARENT');
      VueStore.commit('auth/setAccess', selectedStudentById.access);
      VueStore.commit('auth/setApplications', selectedStudentById.applications);
    }

    if (VueStore.getters['auth/isOfficeLogin']) {
      VueStore.commit('auth/clearAuth');
      VueStore.dispatch('auth/clearStore');
      next({ name: 'Auth.Login' });
    } else next();
  },


  watch: {
    expandAdditionalData(newValue) {
      if (!newValue) {
        this.selectedChildren = [{ id: this.student.id, firstName: this.student.firstName }];
      }
      this.getExpandAdditionalDataError();
    },
    selectedChildren: 'getExpandAdditionalDataError',
  },

  async created() {
    this.closeSidebar();
    this.$store.commit('layout/startLoading');
    try {
      const initialData = await this.getRefundData({ kidId: this.student.id });
      this.returnsName = initialData?.results?.fullName;
      this.bankAccount = initialData?.results?.bankAccountNumber;
      this.selectedChildren.push({ id: this.student.id, firstName: this.student.firstName });
      this.isEditing = !initialData?.results;
    } catch (error) {
      this.hasInitialData = false;
      console.error('Error fetching refund data: ', error);
    } finally {
      this.$store.commit('layout/finishLoading');
    }
  },

  mounted() {
    document.body.classList.add('hide-class-list');
    document.body.classList.add('dont-show-class-list');
    setTimeout(() => {
      this.$store.commit('layout/finishLoading');
    }, 300);
  },
  beforeDestroy() {
    document.body.classList.remove('dont-show-class-list');
  },
};
</script>

<style lang="scss" scoped>
.es-logo {
  display: none;

  @media screen and (min-width: 768px) {
    display: block;
    max-width: 11rem;
  }
}

.error-wrapper {
  display: flex;
  gap: 0.5rem;
  @apply text-sm mb-2;
  line-height: 18px;
  color: #ab2017;
}

.submit-button {
  @media screen and (min-width: 768px) {
    max-width: 15rem;
    margin: 0 auto;
  }
}

.payments-return-section {
  margin-left: auto;
  overflow: hidden;
  min-height: 100%;
  position: relative;
  z-index: 1;

  @media screen and (min-width: 768px) {
    width: calc(100% - 312px);
    padding-top: 30px;
  }

  .payments-return-wrapper {
    @apply flex flex-col justify-center items-center;
    gap: 2.5rem;
  }

  .notification-title {
    font-size: 16px;
    font-style: normal;
    font-weight: 500;
    line-height: 20px;
    color: #111724;
  }

  .notification-list {
    margin-top: 0.25rem;
    margin-left: 0.25rem;
    list-style: inside;
  }

  .notification-list-item {
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 18px;
    color: #2e384a;
  }

  .success-notification {
    margin-top: 2rem;
  }

  .error-notification {
    margin-top: 2rem;
    margin-bottom: 2rem;
  }

  .payments-return {
    @apply relative z-10 flex flex-col bg-white;
    border-radius: 10px;
    max-width: 650px;
    padding-left: 1rem;
    padding-right: 1rem;
    padding-top: 8rem;
    padding-bottom: 8rem;
    min-height: 100vh;

    @media screen and (min-width: 768px) {
      padding-top: 3rem;
      padding-bottom: 3rem;
      padding-left: 4rem;
      padding-right: 4rem;
      min-height: unset;
    }

    &__description-wrapper {
      align-items: flex-start;
      display: flex;
      gap: 0.75rem;
    }

    &__description {
      @apply font-normal text-grey-2;
      line-height: 20px;
    }

    &__logo {
      @apply flex flex-col;
      gap: 2rem;

      @media screen and (min-width: 768px) {
        gap: 3rem;
      }
    }

    &__title {
      @apply font-bold text-2xl text-center block;

      @media screen and (min-width: 700px) {
        @apply text-4xl;
      }
    }

    &__description-wrapper {
      align-items: flex-start;
      display: flex;
      gap: 0.75rem;
    }

    &__inputs {
      margin-top: 48px;
    }

    &__checkbox-wrapper {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      margin-bottom: 1.5rem;
      cursor: pointer;
    }

    &__checkbox {
      appearance: none;
      cursor: pointer;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      background: transparent;
      width: 20px;
      height: 20px;
      border-radius: 4px;
      border: 1px solid #cacfd8;
      transition: border-color .2s ease-in-out, background-color .2s ease-in-out;

      &::before {
        content: "";
        width: 20px;
        height: 20px;
        mask-position: center center;
        // TODO: fix checkmark
        mask-image: url("data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAxNiAxNiIgZmlsbD0iY3VycmVudENvbG9yIiBjbGFzcz0idy00IGgtNCI+CiAgPHBhdGggZmlsbC1ydWxlPSJldmVub2RkIiBkPSJNMTIuNDE2IDMuMzc2YS43NS43NSAwIDAgMSAuMjA4IDEuMDRsLTUgNy41YS43NS43NSAwIDAgMS0xLjE1NC4xMTRsLTMtM2EuNzUuNzUgMCAwIDEgMS4wNi0xLjA2bDIuMzUzIDIuMzUzIDQuNDkzLTYuNzRhLjc1Ljc1IDAgMCAxIDEuMDQtLjIwN1oiIGNsaXAtcnVsZT0iZXZlbm9kZCIgLz4KPC9zdmc+Cg==");
        background: #fff;
        opacity: 0;
      }

      &:hover {
        border-color: #2E384A;
      }

      &:checked {
        background: #111724;
        border-color: #111724;

        &::before {
          opacity: 1;
        }
      }
    }

    &__additional {
      padding-bottom: 4rem;
    }

    &__additional-inputs {
      display: flex;
      flex-wrap: wrap;
      gap: 2rem;
      padding-top: 1rem;
    }

    &__data {
      display: flex;
      flex-direction: column;
      gap: 2rem;
      margin-top: 4rem;
      margin-bottom: 4rem;
    }

    &__info {
      display: flex;
      flex-direction: column;
      gap: 0.25rem;

      .label {
        font-size: 16px;
        font-style: normal;
        font-weight: 400;
        line-height: 20px;
        color: #5b657a;
      }

      .value {
        font-size: 18px;
        font-style: normal;
        font-weight: 500;
        line-height: 24px;
        color: #111724;
      }
    }
  }
}
</style>
